import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";

// Type definitions
type ServerType = "login" | "taxi" | "payment";

interface ApiRequestParams {
  server: ServerType;
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: Record<string, any> | null;
  params?: Record<string, any>;
  headers?: Record<string, string>;
}

// Retrieve settings from environment variables
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "default_secure_key";
const ENABLE_ENCRYPTION = process.env.REACT_APP_ENABLE_ENCRYPTION === "true";

// Define API Base URLs
const urlList: Record<ServerType, string | undefined> = {
  login: process.env.REACT_APP_LOGIN_API_URL,
  taxi: process.env.REACT_APP_TAXI_API_URL,
  payment: process.env.REACT_APP_PAY_API_URL,
};

/**
 * The store is injected after creation rather than imported, because store.ts
 * imports the slices, which import this module. Importing the store here would
 * close that cycle and leave `store` undefined at module-eval time.
 */
type StoreLike = { getState: () => any; dispatch?: (action: any) => any; subscribe?: (listener: () => void) => () => void };
let storeRef: StoreLike | null = null;
export const injectStore = (store: StoreLike) => {
  storeRef = store;
  keepSessionFresh(store);
};

/**
 * Encrypts data using AES encryption.
 */
const encryptData = (data: Record<string, any>): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

/**
 * Decrypts AES encrypted data.
 */
const decryptData = (encryptedData: string): Record<string, any> | null => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedString ? JSON.parse(decryptedString) : null;
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
};

/**
 * Normalises whatever axios threw into a `{ msg, status }` shape. Every thunk
 * rejects with this, so `action.payload.msg` is always safe to read in a slice.
 */
export interface ApiError {
  msg: string;
  status: number;
}

/** What to say when the server answered without a `msg` of its own. */
const STATUS_MESSAGES: Record<number, string> = {
  401: "Your session has expired. Please sign in again.",
  403: "You don't have access to this.",
  413: "This file is too large to upload.",
  429: "Too many requests — please wait a moment and try again.",
};

export const toApiError = (error: any): ApiError => {
  if (error?.response) {
    const status = error.response.status;
    return {
      status,
      msg: error.response.data?.msg || STATUS_MESSAGES[status] || (status >= 500 ? "Something went wrong on our side. Please try again." : `Error ${status}`),
    };
  }
  if (error?.apiError) return error.apiError;
  // No response at all: offline, DNS, CORS or a timeout.
  if (error?.request || error?.code === "ERR_NETWORK") {
    return { status: 0, msg: "Can't reach the server. Check your internet connection and try again." };
  }
  return { status: 500, msg: error?.message || "Something went wrong." };
};

// ── Session renewal ──────────────────────────────────────────────────
//
// The access token lasts 24 h and was only renewed when the app loaded, so a
// tab (or the installed app) left open past that answered every save and
// upload with a bare "Error 401". Now: renew shortly before it expires, and on
// a 401 renew once and retry the request. If the refresh token itself is no
// longer valid, end the session cleanly so the supplier signs in again.

const REFRESH_URL = "cust/mobile-refresh-token";
const RENEW_BEFORE_MS = 2 * 60 * 1000;
let renewing: Promise<string | null> | null = null;

/** Milliseconds until a JWT expires (null if it can't be read). */
const msUntilExpiry = (jwt: string): number | null => {
  try {
    const part = jwt.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 - Date.now() : null;
  } catch {
    return null;
  }
};

const sessionExpired = () => {
  storeRef?.dispatch?.({ type: "user/logout" });
};

/**
 * Renew in the background too, not only when a request goes out: a timer fires
 * ~2 min before the token expires (re-armed whenever the token changes), and
 * coming back to the tab / app checks again — phones and background tabs pause
 * timers, so the timer alone can't be trusted.
 */
let renewTimer: ReturnType<typeof setTimeout> | null = null;
let watchedToken: string | null = null;
const renewIfDue = () => {
  const token = storeRef?.getState()?.user?.token;
  if (!token) return;
  const left = msUntilExpiry(token);
  if (left !== null && left < RENEW_BEFORE_MS) renewSession();
};
const armTimer = (token: string | null) => {
  if (renewTimer) clearTimeout(renewTimer);
  renewTimer = null;
  if (!token) return;
  const left = msUntilExpiry(token);
  if (left === null) return;
  // setTimeout overflows past ~24.8 days; the token lives far less, but clamp anyway.
  const wait = Math.min(Math.max(left - RENEW_BEFORE_MS, 0), 2 ** 31 - 1);
  renewTimer = setTimeout(renewIfDue, wait);
};
function keepSessionFresh(store: StoreLike) {
  store.subscribe?.(() => {
    const token = store.getState()?.user?.token || null;
    if (token !== watchedToken) {
      watchedToken = token;
      armTimer(token);
    }
  });
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") renewIfDue();
    });
  }
  if (typeof window !== "undefined") window.addEventListener("online", renewIfDue);
}

/** Renew the access token with the stored refresh token; one renewal at a time. */
const renewSession = (): Promise<string | null> => {
  if (renewing) return renewing;
  renewing = (async () => {
    let refresh: string | null = null;
    try {
      refresh = localStorage.getItem("refreshToken");
    } catch {
      refresh = null;
    }
    if (!refresh || refresh === "undefined") {
      sessionExpired();
      return null;
    }
    try {
      const res = await send({ server: "login", url: REFRESH_URL, method: "POST", data: { token: refresh } }, "");
      const token = res?.data?.access_token;
      if (res?.status === "success" && token) {
        // Same envelope as login: the user slice stores token + user + refresh token.
        storeRef?.dispatch?.({ type: "user/sessionRenewed", payload: res });
        return token as string;
      }
      sessionExpired();
      return null;
    } catch (err: any) {
      // The server said no (expired / revoked): sign out. A network failure
      // leaves the session alone — it may work again once back online.
      if (err?.response) sessionExpired();
      return null;
    }
  })().finally(() => {
    renewing = null;
  });
  return renewing;
};

/**
 * Secure API request handler with automatic encryption & decryption.
 *
 * Unlike the web app's version this does not dispatch PENDING/SUCCESS/FAILURE
 * itself — createAsyncThunk generates those three lifecycle actions, so the
 * transport layer stays a plain promise. It throws on failure; the thunk
 * wrapper below converts that into a typed rejection.
 */
/** One HTTP round trip with the given access token ("" = none). */
const send = async (
  { server, url, method = "GET", data, params, headers }: ApiRequestParams,
  accessToken: string
): Promise<any> => {
  if (!urlList[server]) throw new Error("Invalid server selection.");
  const baseURL = urlList[server] as string;
  const token = accessToken ? `Bearer ${accessToken}` : "";

  // Device ID management
  let deviceId = localStorage.getItem("x-deviceid");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("x-deviceid", deviceId);
  }

  // Ensure proper encryption structure
  let encryptedData: {} | null = null;
  const isEncrypted = ENABLE_ENCRYPTION;

  // A FormData body must be sent as multipart, with the browser setting its own
  // boundary — so Content-Type is omitted below when uploading. Encryption is
  // also skipped: the payload is a file, not a JSON envelope.
  const isMultipart = typeof FormData !== "undefined" && data instanceof FormData;


  if (data && isEncrypted && !isMultipart) {
    encryptedData = { encrypted: encryptData(data) };
  }

  // Secure headers
  const headersData: Record<string, string> = {
    ...(isMultipart ? {} : { "Content-Type": "application/json" }),
    "X-Device-ID": deviceId,
    Authorization: token,
    "X-Encrypted": isEncrypted && !isMultipart ? "true" : "false",
    "X-Frame-Options": "DENY",
    "Content-Security-Policy": "default-src 'self'",
  };

  // Merge additional headers
  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      headersData[key] = value;
    });
  }

  const options: AxiosRequestConfig = {
    baseURL,
    method,
    url: url.trim(),
    data: isEncrypted && !isMultipart ? encryptedData : data,
    headers: headersData,
    params,
    withCredentials: true,
  };

  const response: AxiosResponse = await axios(options);

  // Ensure response decryption if necessary
  let responseData: any = response?.data;
  if (response.headers["x-encrypted"] === "true" && responseData?.encrypted) {
    responseData = decryptData(responseData.encrypted);
  }

  return responseData;
};

/**
 * Every API call goes through here: attaches the session token, renews it when
 * it's about to expire, and on a 401 renews once and retries (FormData uploads
 * included — the same body is sent again).
 */
export const MakeApiRequest = async (request: ApiRequestParams): Promise<any> => {
  const isAuthCall = request.url.includes(REFRESH_URL) || request.url.includes("cust/login");
  let token: string = storeRef?.getState()?.user?.token || "";

  if (token && !isAuthCall) {
    const left = msUntilExpiry(token);
    if (left !== null && left < RENEW_BEFORE_MS) token = (await renewSession()) || token;
  }

  try {
    return await send(request, token);
  } catch (err: any) {
    if (err?.response?.status !== 401 || !token || isAuthCall) throw err;
    const renewed = await renewSession();
    if (!renewed) {
      const expired: any = new Error("Your session has expired. Please sign in again.");
      expired.apiError = { status: 401, msg: "Your session has expired. Please sign in again." };
      throw expired;
    }
    return send(request, renewed);
  }
};
